import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import Sidebar from '../components/layout/Sidebar';
import { 
  Truck, Package, Building2, Search, Plus, Minus, Send, 
  History, BarChart3, AlertCircle, CheckCircle2, X, Download, FileText
} from 'lucide-react';

const BranchDistribution = () => {
  const { api, user } = useAuth();
  const { isMatriz } = useBranch();
  
  // States
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('transfer'); // transfer, history, stats
  const [branches, setBranches] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [areas, setAreas] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Transfer form states
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [transferItems, setTransferItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Check if user can access this module
  const canAccess = user && (user.role === 'admin' || user.role === 'gerente' || user.role === 'administrador') && !user.branch_id;

  useEffect(() => {
    if (canAccess) {
      fetchData();
    }
  }, [canAccess]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchTransfers();
    } else if (activeTab === 'stats') {
      fetchStats();
    }
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [branchesRes, suppliesRes, areasRes] = await Promise.all([
        api.get('/branches'),
        api.get('/distribution/catalog'),
        api.get('/inventory/areas')
      ]);
      
      // Filter out Matriz from branch list
      const nonMatrizBranches = branchesRes.data.filter(b => !b.is_matriz);
      setBranches(nonMatrizBranches);
      setSupplies(suppliesRes.data);
      setAreas(areasRes.data);
      
      // Set default area (Almacén General)
      const almacen = areasRes.data.find(a => a.name.toLowerCase().includes('almac'));
      if (almacen) {
        setSelectedArea(almacen.area_id);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransfers = async () => {
    try {
      const res = await api.get('/distribution/transfers?days=30');
      setTransfers(res.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/distribution/stats?days=30');
      setStats(res.data);
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    }
  };

  const handleAddItem = (supply) => {
    const existing = transferItems.find(i => i.supply_id === supply.supply_id);
    if (existing) {
      toast.info('Este insumo ya está en la lista');
      return;
    }
    
    setTransferItems([...transferItems, {
      supply_id: supply.supply_id,
      supply_name: supply.name,
      quantity: 1,
      unit: supply.unit,
      cost_per_unit: supply.cost_per_unit || 0,
      available_stock: supply.stock || 0
    }]);
  };

  const handleUpdateQuantity = (supplyId, newQuantity) => {
    if (newQuantity < 0) return;
    setTransferItems(transferItems.map(item => 
      item.supply_id === supplyId 
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const handleRemoveItem = (supplyId) => {
    setTransferItems(transferItems.filter(i => i.supply_id !== supplyId));
  };

  const handleSubmitTransfer = async () => {
    if (!selectedBranch) {
      toast.error('Selecciona una sucursal destino');
      return;
    }
    if (transferItems.length === 0) {
      toast.error('Agrega al menos un insumo');
      return;
    }
    
    const branch = branches.find(b => b.branch_id === selectedBranch);
    
    setSubmitting(true);
    try {
      const response = await api.post('/distribution/transfer', {
        to_branch_id: selectedBranch,
        to_branch_name: branch?.name || 'Sucursal',
        from_area_id: selectedArea,
        items: transferItems.map(item => ({
          supply_id: item.supply_id,
          supply_name: item.supply_name,
          quantity: item.quantity,
          unit: item.unit,
          cost_per_unit: item.cost_per_unit
        })),
        notes: notes
      });
      
      toast.success(`Distribución enviada a ${branch?.name}`);
      
      // Open PDF in new tab
      const transferId = response.data.transfer_id;
      if (transferId) {
        const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/distribution/transfer/${transferId}/pdf`;
        window.open(pdfUrl, '_blank');
      }
      
      setTransferItems([]);
      setNotes('');
      setSelectedBranch('');
      fetchData(); // Refresh stock
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al realizar distribución');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Download PDF for a specific transfer
  const handleDownloadPDF = (transferId) => {
    const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/distribution/transfer/${transferId}/pdf`;
    window.open(pdfUrl, '_blank');
  };

  // Filter supplies by search
  const filteredSupplies = supplies.filter(s => 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate totals
  const totalItems = transferItems.length;
  const totalQuantity = transferItems.reduce((sum, i) => sum + i.quantity, 0);
  const totalCost = transferItems.reduce((sum, i) => sum + (i.quantity * i.cost_per_unit), 0);

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex" data-testid="distribution-access-denied">
        <Sidebar />
        <main className="flex-1 md:ml-64 p-6 pt-16 md:pt-6">
          <div className="max-w-4xl mx-auto">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-8 text-center">
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-red-700 mb-2" data-testid="access-denied-title">Acceso Restringido</h2>
                <p className="text-red-600" data-testid="access-denied-message">
                  Solo usuarios de Matriz (administradores o gerentes) pueden acceder a este módulo.
                </p>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="distribution-page">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-6 pt-16 md:pt-6">
        <div className="max-w-7xl mx-auto" style={{ minHeight: '100vh' }}>
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3" data-testid="distribution-title">
              <Truck className="w-8 h-8 text-blue-600" />
              Distribución a Sucursales
            </h1>
            <p className="text-gray-600 mt-1" data-testid="distribution-subtitle">
              Envía insumos desde Matriz a las sucursales
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 bg-white rounded-lg p-1 shadow-sm w-fit" data-testid="distribution-tabs">
            {[
              { id: 'transfer', label: 'Nueva Distribución', icon: Send },
              { id: 'history', label: 'Historial', icon: History },
              { id: 'stats', label: 'Estadísticas', icon: BarChart3 }
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden md:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* TAB: Nueva Distribución */}
          <div style={{ display: activeTab === 'transfer' ? 'block' : 'none' }} data-testid="transfer-tab-content">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Supplies Catalog */}
              <div className="lg:col-span-2">
                <Card data-testid="supplies-catalog-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      Catálogo de Insumos
                    </CardTitle>
                    <div className="relative mt-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Buscar insumo..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="supply-search-input"
                      />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[500px] overflow-y-auto" data-testid="supplies-list">
                      {loading ? (
                        <div className="text-center py-8 text-gray-500" data-testid="loading-indicator">Cargando...</div>
                      ) : filteredSupplies.length === 0 ? (
                        <div className="text-center py-8 text-gray-500" data-testid="no-supplies-message">No hay insumos</div>
                      ) : (
                        <div className="space-y-2">
                          {filteredSupplies.map(supply => (
                            <div 
                              key={supply.supply_id}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                              data-testid={`supply-item-${supply.supply_id}`}
                            >
                              <div>
                                <p className="font-medium" data-testid="supply-name">{supply.name}</p>
                                <p className="text-sm text-gray-500">
                                  {supply.category} • ${supply.cost_per_unit?.toFixed(2) || '0.00'}/{supply.unit}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleAddItem(supply)}
                                disabled={transferItems.some(i => i.supply_id === supply.supply_id)}
                                data-testid={`add-supply-btn-${supply.supply_id}`}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right: Transfer Form */}
              <div className="space-y-4" data-testid="transfer-form">
                {/* Branch Selection */}
                <Card data-testid="destination-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Building2 className="w-5 h-5" />
                      Destino
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Sucursal Destino</label>
                      <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                        <SelectTrigger data-testid="branch-select-trigger">
                          <SelectValue placeholder="Seleccionar sucursal" />
                        </SelectTrigger>
                        <SelectContent>
                          {branches.map(branch => (
                            <SelectItem key={branch.branch_id} value={branch.branch_id} data-testid={`branch-option-${branch.branch_id}`}>
                              {branch.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700">Área Origen (Matriz)</label>
                      <Select value={selectedArea} onValueChange={setSelectedArea}>
                        <SelectTrigger data-testid="area-select-trigger">
                          <SelectValue placeholder="Seleccionar área" />
                        </SelectTrigger>
                        <SelectContent>
                          {areas.map(area => (
                            <SelectItem key={area.area_id} value={area.area_id} data-testid={`area-option-${area.area_id}`}>
                              {area.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Transfer Items */}
                <Card data-testid="transfer-items-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg" data-testid="transfer-items-count">Insumos a Enviar ({totalItems})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {transferItems.length === 0 ? (
                      <div className="text-center py-6 text-gray-500" data-testid="empty-transfer-items">
                        <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>Selecciona insumos del catálogo</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[300px] overflow-y-auto" data-testid="transfer-items-list">
                        {transferItems.map(item => (
                          <div key={item.supply_id} className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg" data-testid={`transfer-item-${item.supply_id}`}>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{item.supply_name}</p>
                              <p className="text-xs text-gray-500">${item.cost_per_unit?.toFixed(2)}/{item.unit}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => handleUpdateQuantity(item.supply_id, item.quantity - 1)}
                                data-testid={`decrease-qty-${item.supply_id}`}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => handleUpdateQuantity(item.supply_id, parseFloat(e.target.value) || 0)}
                                className="w-16 h-7 text-center text-sm"
                                data-testid={`qty-input-${item.supply_id}`}
                              />
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => handleUpdateQuantity(item.supply_id, item.quantity + 1)}
                                data-testid={`increase-qty-${item.supply_id}`}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-red-500"
                                onClick={() => handleRemoveItem(item.supply_id)}
                                data-testid={`remove-item-${item.supply_id}`}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Notes */}
                    <div className="mt-4">
                      <Input
                        placeholder="Notas (opcional)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        data-testid="transfer-notes-input"
                      />
                    </div>

                    {/* Summary */}
                    {transferItems.length > 0 && (
                      <div className="mt-4 p-3 bg-gray-100 rounded-lg" data-testid="transfer-summary">
                        <div className="flex justify-between text-sm">
                          <span>Total Unidades:</span>
                          <span className="font-medium" data-testid="total-quantity">{totalQuantity.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-1">
                          <span>Costo Total:</span>
                          <span className="font-bold text-blue-600" data-testid="total-cost">${totalCost.toFixed(2)}</span>
                        </div>
                      </div>
                    )}

                    {/* Submit Button */}
                    <Button
                      className="w-full mt-4"
                      disabled={!selectedBranch || transferItems.length === 0 || submitting}
                      onClick={handleSubmitTransfer}
                      data-testid="submit-transfer-btn"
                    >
                      {submitting ? 'Enviando...' : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Enviar Distribución
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* TAB: History */}
          <div style={{ display: activeTab === 'history' ? 'block' : 'none' }} data-testid="history-tab-content">
            <Card>
              <CardHeader>
                <CardTitle data-testid="history-title">Historial de Distribuciones (Últimos 30 días)</CardTitle>
              </CardHeader>
              <CardContent>
                {transfers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500" data-testid="no-transfers-message">
                    No hay distribuciones registradas
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="transfers-list">
                    {transfers.map(transfer => (
                      <div key={transfer.transfer_id} className="p-4 bg-gray-50 rounded-lg" data-testid={`transfer-record-${transfer.transfer_id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="w-5 h-5 text-green-500" />
                              <span className="font-medium" data-testid="transfer-branch-name">{transfer.to_branch_name}</span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1" data-testid="transfer-date">
                              {new Date(transfer.created_at).toLocaleDateString('es-MX', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                              {' • '}Por: {transfer.created_by_name}
                            </p>
                            <p className="text-sm mt-2" data-testid="transfer-items-info">
                              {transfer.total_items} insumos • {transfer.total_quantity?.toFixed(2)} unidades
                            </p>
                          </div>
                          <div className="text-right flex flex-col items-end gap-2">
                            <p className="font-bold text-blue-600" data-testid="transfer-cost">${transfer.total_cost?.toFixed(2)}</p>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadPDF(transfer.transfer_id)}
                              className="text-xs"
                              data-testid={`download-pdf-${transfer.transfer_id}`}
                            >
                              <FileText className="w-3 h-3 mr-1" />
                              PDF
                            </Button>
                          </div>
                        </div>
                        {transfer.notes && (
                          <p className="text-sm text-gray-600 mt-2 italic" data-testid="transfer-notes">&ldquo;{transfer.notes}&rdquo;</p>
                        )}
                        {/* Items preview */}
                        <div className="mt-3 flex flex-wrap gap-1" data-testid="transfer-items-preview">
                          {transfer.items?.slice(0, 5).map((item, idx) => (
                            <span key={idx} className="text-xs bg-white px-2 py-1 rounded">
                              {item.supply_name} ({item.quantity} {item.unit})
                            </span>
                          ))}
                          {transfer.items?.length > 5 && (
                            <span className="text-xs text-gray-500">+{transfer.items.length - 5} más</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* TAB: Stats */}
          <div style={{ display: activeTab === 'stats' ? 'block' : 'none' }} data-testid="stats-tab-content">
            {stats && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="stats-summary-cards">
                  <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white" data-testid="total-transfers-card">
                    <CardContent className="p-6">
                      <p className="text-blue-100">Total Distribuciones</p>
                      <p className="text-3xl font-bold mt-1" data-testid="total-transfers-value">{stats.total_transfers}</p>
                      <p className="text-blue-100 text-sm mt-1">Últimos 30 días</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white" data-testid="total-items-card">
                    <CardContent className="p-6">
                      <p className="text-green-100">Items Distribuidos</p>
                      <p className="text-3xl font-bold mt-1" data-testid="total-items-value">{stats.total_items_distributed}</p>
                      <p className="text-green-100 text-sm mt-1">Insumos enviados</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white" data-testid="total-cost-card">
                    <CardContent className="p-6">
                      <p className="text-purple-100">Costo Total</p>
                      <p className="text-3xl font-bold mt-1" data-testid="total-cost-value">${stats.total_cost_distributed?.toFixed(2)}</p>
                      <p className="text-purple-100 text-sm mt-1">Valor distribuido</p>
                    </CardContent>
                  </Card>
                </div>

                {/* By Branch */}
                <Card data-testid="stats-by-branch-card">
                  <CardHeader>
                    <CardTitle data-testid="by-branch-title">Distribución por Sucursal</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats.by_branch?.length === 0 ? (
                      <p className="text-gray-500 text-center py-4" data-testid="no-branch-stats">No hay datos</p>
                    ) : (
                      <div className="space-y-3" data-testid="branch-stats-list">
                        {stats.by_branch?.map(branch => (
                          <div key={branch.branch_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`branch-stat-${branch.branch_id}`}>
                            <div className="flex items-center gap-3">
                              <Building2 className="w-5 h-5 text-gray-400" />
                              <div>
                                <p className="font-medium" data-testid="branch-stat-name">{branch.branch_name}</p>
                                <p className="text-sm text-gray-500" data-testid="branch-stat-details">
                                  {branch.total_transfers} envíos • {branch.total_items} items
                                </p>
                              </div>
                            </div>
                            <p className="font-bold text-blue-600" data-testid="branch-stat-cost">${branch.total_cost?.toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default BranchDistribution;
